import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  getChartOfAccounts, 
  createAccount, 
  updateAccount, 
  deactivateAccount, 
  getAccountTemplates, 
  applyAccountTemplate, 
  generateTrialBalance, 
  subscribeToChartChanges, 
  getAccountHierarchy,
  getAccountBalances,
  validateIFRSCompliance,
  ACCOUNT_TYPE_OPTIONS, 
  ACCOUNT_SUBTYPE_OPTIONS,
  IFRS_SECTIONS 
} from '../../../services/chartOfAccountsService';

const ChartOfAccounts = () => {
  // Enhanced auth context with better null handling
  const authContext = useAuth();
  
  const { 
    userProfile = null, 
    isAuthenticated = false, 
    profileLoading = false 
  } = authContext || {};

  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [accountHierarchy, setAccountHierarchy] = useState([]);
  const [trialBalance, setTrialBalance] = useState([]);
  const [accountBalances, setAccountBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTrialBalance, setShowTrialBalance] = useState(false);
  const [showIFRSCompliance, setShowIFRSCompliance] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterIFRSSection, setFilterIFRSSection] = useState('all');
  const [viewMode, setViewMode] = useState('flat'); // 'flat' | 'hierarchy' | 'ifrs'
  const [newAccount, setNewAccount] = useState({
    account_code: '',
    account_name: '',
    account_description: '',
    account_type: '',
    account_subtype: '',
    parent_account_id: null,
    ifrs_section: '',
    ifrs_subsection: '',
    is_control_account: false,
    is_bank_account: false,
    is_cash_account: false,
    allows_posting: true,
    opening_balance: 0
  });

  // Get organization ID with enhanced fallback logic
  const organizationId = userProfile?.organization_id || 
                         userProfile?.organizations?.[0]?.organization_id ||
                         userProfile?.organizations?.[0]?.id ||
                         userProfile?.default_organization_id ||
                         null;

  // Enhanced loading and auth management
  useEffect(() => {
    if (!authContext) {
      setError('Authentication context unavailable');
      setLoading(false);
      return;
    }

    if (isAuthenticated && !profileLoading) {
      if (organizationId) {
        loadAllAccountingData();
        
        // Real-time subscription
        const unsubscribe = subscribeToChartChanges(organizationId, (payload) => {
          console.debug('Chart of accounts updated:', payload);
          loadChartOfAccounts(); // Refresh on changes
        });
        
        return unsubscribe;
      } else if (userProfile) {
        setError('No organization access found. Please contact your administrator.');
        setLoading(false);
      }
    } else if (!isAuthenticated && !profileLoading) {
      setError('Please log in to access Chart of Accounts.');
      setLoading(false);
    }
  }, [isAuthenticated, organizationId, profileLoading, userProfile, authContext]);

  const loadAllAccountingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await Promise.all([
        loadChartOfAccounts(),
        loadAccountTemplates(),
        loadAccountHierarchy(),
        loadAccountBalances()
      ]);
      
    } catch (err) {
      console.error('Error loading accounting data:', err);
      setError('Failed to load accounting data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadChartOfAccounts = async () => {
    if (!organizationId) return;
    
    const { data, error: fetchError } = await getChartOfAccounts(organizationId);
    
    if (fetchError) {
      setError(`Failed to load chart of accounts: ${fetchError?.message}`);
      return;
    }
    
    setAccounts(data || []);
  };

  const loadAccountTemplates = async () => {
    try {
      setError(null); // Clear any previous errors
      
      const { data, error: fetchError } = await getAccountTemplates('INT');
      
      if (fetchError) {
        console.error('Failed to load account templates:', fetchError);
        setError(`Failed to load templates: ${fetchError?.message}`);
        return;
      }
      
      console.debug('Loaded templates:', data); // Add debug logging
      setTemplates(data || []);
      
      // If no templates found, show informative message
      if (!data || data?.length === 0) {
        console.warn('No IFRS templates found in database');
        setError('No IFRS templates available. Please contact your administrator to set up templates.');
      }
      
    } catch (err) {
      console.error('Unexpected error loading templates:', err);
      setError('Unable to load IFRS templates. Please try again.');
    }
  };

  const loadAccountHierarchy = async () => {
    if (!organizationId) return;
    
    const { data, error: fetchError } = await getAccountHierarchy(organizationId);
    
    if (fetchError) {
      console.warn('Failed to load account hierarchy:', fetchError);
      return;
    }
    
    setAccountHierarchy(data || []);
  };

  const loadAccountBalances = async () => {
    if (!organizationId) return;
    
    const { data, error: fetchError } = await getAccountBalances(organizationId);
    
    if (fetchError) {
      console.warn('Failed to load account balances:', fetchError);
      return;
    }
    
    setAccountBalances(data || []);
  };

  const handleAddAccount = async () => {
    try {
      if (!newAccount?.account_code || !newAccount?.account_name || !newAccount?.account_type) {
        setError('Please fill in all required fields (Code, Name, Type)');
        return;
      }

      if (!organizationId) {
        setError('Organization ID is required');
        return;
      }

      setLoading(true);
      setError(null);
      
      const accountData = {
        ...newAccount,
        organization_id: organizationId,
        opening_balance: parseFloat(newAccount?.opening_balance) || 0
      };

      const { data, error: createError } = await createAccount(accountData);
      
      if (createError) {
        setError(`Failed to create account: ${createError?.message}`);
        return;
      }

      setSuccess('Account created successfully!');
      resetForm();
      await loadAllAccountingData();
      
    } catch (err) {
      console.error('Error creating account:', err);
      setError('Failed to create account');
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setNewAccount({
      account_code: account?.account_code || '',
      account_name: account?.account_name || '',
      account_description: account?.account_description || '',
      account_type: account?.account_type || '',
      account_subtype: account?.account_subtype || '',
      parent_account_id: account?.parent_account_id || null,
      ifrs_section: account?.ifrs_section || '',
      ifrs_subsection: account?.ifrs_subsection || '',
      is_control_account: account?.is_control_account || false,
      is_bank_account: account?.is_bank_account || false,
      is_cash_account: account?.is_cash_account || false,
      allows_posting: account?.allows_posting !== false,
      opening_balance: account?.opening_balance || 0
    });
    setShowAddForm(true);
  };

  const handleUpdateAccount = async () => {
    try {
      if (!editingAccount?.id) {
        setError('Account ID is required for update');
        return;
      }

      setLoading(true);
      setError(null);
      
      const updates = {
        ...newAccount,
        opening_balance: parseFloat(newAccount?.opening_balance) || 0
      };

      const { data, error: updateError } = await updateAccount(editingAccount?.id, updates);
      
      if (updateError) {
        setError(`Failed to update account: ${updateError?.message}`);
        return;
      }

      setSuccess('Account updated successfully!');
      resetForm();
      await loadAllAccountingData();
      
    } catch (err) {
      console.error('Error updating account:', err);
      setError('Failed to update account');
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDeactivateAccount = async (accountId) => {
    try {
      if (!window?.confirm('Are you sure you want to deactivate this account?')) {
        return;
      }

      setLoading(true);
      
      const { error: deactivateError } = await deactivateAccount(accountId);
      
      if (deactivateError) {
        setError(`Failed to deactivate account: ${deactivateError?.message}`);
        return;
      }

      setSuccess('Account deactivated successfully!');
      await loadAllAccountingData();
      
    } catch (err) {
      console.error('Error deactivating account:', err);
      setError('Failed to deactivate account');
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleApplyTemplate = async () => {
    try {
      if (!selectedTemplate) {
        setError('Please select a template');
        return;
      }

      if (!organizationId) {
        setError('Organization ID is required');
        return;
      }

      setApplyingTemplate(true);
      setError(null);
      
      const { error: applyError } = await applyAccountTemplate(organizationId, selectedTemplate);
      
      if (applyError) {
        setError(`Failed to apply template: ${applyError?.message}`);
        return;
      }

      setSuccess('IFRS-compliant chart of accounts applied successfully!');
      setShowTemplateModal(false);
      setSelectedTemplate('');
      await loadAllAccountingData();
      
    } catch (err) {
      console.error('Error applying template:', err);
      setError('Failed to apply template');
    } finally {
      setApplyingTemplate(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleGenerateTrialBalance = async () => {
    try {
      if (!organizationId) {
        setError('Organization ID is required');
        return;
      }

      setLoading(true);
      setError(null);
      
      const { data: balanceData, error: balanceError } = await generateTrialBalance(organizationId);
      
      if (balanceError) {
        setError(`Failed to generate trial balance: ${balanceError?.message}`);
        return;
      }

      setTrialBalance(balanceData || []);
      setShowTrialBalance(true);
      
    } catch (err) {
      console.error('Error generating trial balance:', err);
      setError('Failed to generate trial balance');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateIFRSCompliance = async () => {
    try {
      if (!organizationId) {
        setError('Organization ID is required');
        return;
      }

      setLoading(true);
      setError(null);
      
      const { data: complianceData, error: complianceError } = await validateIFRSCompliance(organizationId);
      
      if (complianceError) {
        setError(`IFRS validation failed: ${complianceError?.message}`);
        return;
      }

      if (complianceData?.is_compliant) {
        setSuccess('Chart of Accounts is IFRS compliant! ✅');
      } else {
        setError(`IFRS compliance issues found: ${complianceData?.issues?.join(', ')}`);
      }
      
    } catch (err) {
      console.error('Error validating IFRS compliance:', err);
      setError('Failed to validate IFRS compliance');
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const resetForm = () => {
    setNewAccount({
      account_code: '',
      account_name: '',
      account_description: '',
      account_type: '',
      account_subtype: '',
      parent_account_id: null,
      ifrs_section: '',
      ifrs_subsection: '',
      is_control_account: false,
      is_bank_account: false,
      is_cash_account: false,
      allows_posting: true,
      opening_balance: 0
    });
    setShowAddForm(false);
    setEditingAccount(null);
  };

  // Enhanced filtering and grouping
  const filteredAccounts = accounts?.filter(account => {
    const matchesSearch = !searchQuery || 
      account?.account_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      account?.account_code?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      account?.ifrs_section?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesType = filterType === 'all' || account?.account_type === filterType;
    const matchesStatus = filterStatus === 'all' || account?.status === filterStatus;
    const matchesIFRS = filterIFRSSection === 'all' || account?.ifrs_section === filterIFRSSection;
    
    return matchesSearch && matchesType && matchesStatus && matchesIFRS;
  });

  // Group accounts based on view mode
  const getGroupedAccounts = () => {
    if (viewMode === 'ifrs') {
      return filteredAccounts?.reduce((groups, account) => {
        const section = account?.ifrs_section || 'Other';
        if (!groups?.[section]) {
          groups[section] = [];
        }
        groups?.[section]?.push(account);
        return groups;
      }, {});
    } else if (viewMode === 'hierarchy') {
      // For hierarchy view, we'd use the hierarchy data
      return accountHierarchy?.reduce((groups, account) => {
        const level = `Level ${account?.level || 0}`;
        if (!groups?.[level]) {
          groups[level] = [];
        }
        groups?.[level]?.push(account);
        return groups;
      }, {});
    } else {
      // Default flat view grouped by type
      return filteredAccounts?.reduce((groups, account) => {
        const type = account?.account_type || 'other';
        if (!groups?.[type]) {
          groups[type] = [];
        }
        groups?.[type]?.push(account);
        return groups;
      }, {});
    }
  };

  const groupedAccounts = getGroupedAccounts();

  const getAccountTypeIcon = (type) => {
    const icons = {
      'asset': 'TrendingUp',
      'liability': 'TrendingDown', 
      'equity': 'DollarSign',
      'revenue': 'ArrowUpCircle',
      'expense': 'ArrowDownCircle'
    };
    return icons?.[type] || 'Circle';
  };

  const getAccountTypeColor = (type) => {
    const colors = {
      'asset': 'text-green-600',
      'liability': 'text-red-600',
      'equity': 'text-blue-600', 
      'revenue': 'text-emerald-600',
      'expense': 'text-orange-600'
    };
    return colors?.[type] || 'text-gray-600';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount || 0);
  };

  // Loading state
  if (loading && accounts?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="Loader2" className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading IFRS Chart of Accounts...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && accounts?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="AlertTriangle" className="w-8 h-8 mx-auto mb-4 text-destructive" />
            <p className="text-destructive font-medium mb-2">Error Loading Chart of Accounts</p>
            <p className="text-muted-foreground text-sm mb-4">{error}</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={loadAllAccountingData} size="sm">
                Try Again
              </Button>
              {!organizationId && (
                <Button variant="outline" onClick={() => window.location.href = '/organization-settings'} size="sm">
                  Organization Settings
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-lg">
      {/* Enhanced Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="TreePine" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">IFRS Chart of Accounts</h2>
              <p className="text-sm text-muted-foreground">
                Complete accounting structure compliant with International Financial Reporting Standards
              </p>
              <div className="flex items-center space-x-4 mt-1">
                <span className="text-xs text-muted-foreground">
                  {accounts?.length || 0} accounts
                </span>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-emerald-600 font-medium">
                  IFRS Compliant
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {accounts?.length === 0 && (
              <Button
                variant="outline"
                onClick={() => setShowTemplateModal(true)}
                iconName="FileSpreadsheet"
                iconPosition="left"
                size="sm"
              >
                Apply IFRS Template
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleValidateIFRSCompliance}
              iconName="Shield"
              iconPosition="left"
              size="sm"
              disabled={loading}
            >
              Validate IFRS
            </Button>
            <Button
              variant="outline"
              onClick={handleGenerateTrialBalance}
              iconName="Calculator"
              iconPosition="left"
              size="sm"
              disabled={loading}
            >
              Trial Balance
            </Button>
            <Button
              variant="default"
              onClick={() => setShowAddForm(true)}
              iconName="Plus"
              iconPosition="left"
              size="sm"
            >
              Add Account
            </Button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
              <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
                <Icon name="X" size={14} />
              </Button>
            </div>
          </div>
        )}

        {success && (
          <div className="mt-4 p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <p className="text-sm text-success">{success}</p>
              <Button variant="ghost" size="sm" onClick={() => setSuccess(null)} className="ml-auto">
                <Icon name="X" size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Enhanced Search, Filters, and View Controls */}
        <div className="space-y-4 mb-6">
          <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1">
              <Input
                placeholder="Search accounts by name, code, or IFRS section..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                iconName="Search"
              />
            </div>
            
            <Select
              value={filterType}
              onChange={setFilterType}
              options={[
                { value: 'all', label: 'All Types' },
                ...ACCOUNT_TYPE_OPTIONS
              ]}
              className="lg:w-48"
            />
            
            <Select
              value={filterIFRSSection}
              onChange={setFilterIFRSSection}
              options={[
                { value: 'all', label: 'All IFRS Sections' },
                ...IFRS_SECTIONS?.map(section => ({ value: section, label: section }))
              ]}
              className="lg:w-48"
            />
            
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              className="lg:w-48"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2 pb-2 border-b border-border">
            <span className="text-sm font-medium text-muted-foreground">View:</span>
            {[
              { value: 'flat', label: 'By Type', icon: 'Grid3x3' },
              { value: 'ifrs', label: 'IFRS Structure', icon: 'BarChart3' },
              { value: 'hierarchy', label: 'Hierarchy', icon: 'GitBranch' }
            ]?.map((view) => (
              <Button
                key={view?.value}
                variant={viewMode === view?.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode(view?.value)}
                iconName={view?.icon}
                iconPosition="left"
              >
                {view?.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Add/Edit Account Form */}
        {showAddForm && (
          <div className="mb-6 p-4 border border-border rounded-lg bg-muted/20">
            <h3 className="text-lg font-medium text-foreground mb-4">
              {editingAccount ? 'Edit Account' : 'Add New IFRS Account'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Account Code *"
                placeholder="e.g., 1100"
                value={newAccount?.account_code}
                onChange={(e) => setNewAccount({...newAccount, account_code: e?.target?.value})}
                required
              />
              <Input
                label="Account Name *"
                placeholder="e.g., Cash at Bank"
                value={newAccount?.account_name}
                onChange={(e) => setNewAccount({...newAccount, account_name: e?.target?.value})}
                required
              />
              <Select
                label="Account Type *"
                options={ACCOUNT_TYPE_OPTIONS}
                value={newAccount?.account_type}
                onChange={(value) => {
                  setNewAccount({
                    ...newAccount, 
                    account_type: value, 
                    account_subtype: '',
                    ifrs_section: value === 'asset' ? 'Current Assets' :
                                  value === 'liability' ? 'Current Liabilities' :
                                  value === 'equity' ? 'Equity' :
                                  value === 'revenue' ? 'Revenue' :
                                  value === 'expense' ? 'Expenses' : ''
                  });
                }}
                required
              />
              
              {newAccount?.account_type && (
                <Select
                  label="Account Subtype *"
                  options={ACCOUNT_SUBTYPE_OPTIONS?.[newAccount?.account_type] || []}
                  value={newAccount?.account_subtype}
                  onChange={(value) => setNewAccount({...newAccount, account_subtype: value})}
                  required
                />
              )}
              
              <Select
                label="IFRS Section"
                options={IFRS_SECTIONS?.map(section => ({ value: section, label: section }))}
                value={newAccount?.ifrs_section}
                onChange={(value) => setNewAccount({...newAccount, ifrs_section: value})}
              />
              
              <Input
                label="IFRS Subsection"
                placeholder="e.g., Property, Plant and Equipment"
                value={newAccount?.ifrs_subsection}
                onChange={(e) => setNewAccount({...newAccount, ifrs_subsection: e?.target?.value})}
              />
              
              <Input
                label="Description"
                placeholder="Optional description"
                value={newAccount?.account_description}
                onChange={(e) => setNewAccount({...newAccount, account_description: e?.target?.value})}
              />
              
              <Input
                label="Opening Balance"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={newAccount?.opening_balance}
                onChange={(e) => setNewAccount({...newAccount, opening_balance: e?.target?.value})}
              />
            </div>

            {/* Account Properties */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newAccount?.is_control_account}
                  onChange={(e) => setNewAccount({...newAccount, is_control_account: e?.target?.checked})}
                  className="rounded border-border"
                />
                <span className="text-sm">Control Account</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newAccount?.is_bank_account}
                  onChange={(e) => setNewAccount({...newAccount, is_bank_account: e?.target?.checked})}
                  className="rounded border-border"
                />
                <span className="text-sm">Bank Account</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newAccount?.is_cash_account}
                  onChange={(e) => setNewAccount({...newAccount, is_cash_account: e?.target?.checked})}
                  className="rounded border-border"
                />
                <span className="text-sm">Cash Account</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newAccount?.allows_posting}
                  onChange={(e) => setNewAccount({...newAccount, allows_posting: e?.target?.checked})}
                  className="rounded border-border"
                />
                <span className="text-sm">Allow Posting</span>
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="ghost" onClick={resetForm}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={editingAccount ? handleUpdateAccount : handleAddAccount}
                disabled={loading}
              >
                {editingAccount ? 'Update Account' : 'Add Account'}
              </Button>
            </div>
          </div>
        )}

        {/* Accounts List */}
        {accounts?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="TreePine" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Chart of Accounts</h3>
            <p className="text-muted-foreground mb-6">
              Get started by applying an IFRS-compliant template or adding accounts manually.
            </p>
            <div className="flex justify-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowTemplateModal(true)}
                iconName="FileSpreadsheet"
                iconPosition="left"
              >
                Apply IFRS Template
              </Button>
              <Button
                variant="default"
                onClick={() => setShowAddForm(true)}
                iconName="Plus"
                iconPosition="left"
              >
                Add First Account
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object?.entries(groupedAccounts)?.map(([groupName, groupAccounts]) => (
              <div key={groupName} className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/30 px-4 py-3 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={viewMode === 'ifrs' ? 'BarChart3' : getAccountTypeIcon(groupName?.toLowerCase())} 
                        size={16} 
                        className={viewMode === 'ifrs' ? 'text-primary' : getAccountTypeColor(groupName?.toLowerCase())} 
                      />
                      <h3 className="font-medium text-foreground">
                        {groupName} ({groupAccounts?.length})
                      </h3>
                      {viewMode === 'ifrs' && (
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
                          IFRS
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatCurrency(
                        groupAccounts?.reduce((sum, acc) => sum + (parseFloat(acc?.current_balance) || 0), 0)
                      )}
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-border">
                  {groupAccounts?.map((account) => (
                    <div key={account?.id} className="p-4 hover:bg-muted/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                              {account?.account_code}
                            </span>
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground">{account?.account_name}</span>
                              {account?.account_description && (
                                <span className="text-xs text-muted-foreground">{account?.account_description}</span>
                              )}
                              {viewMode === 'ifrs' && account?.ifrs_subsection && (
                                <span className="text-xs text-primary font-medium">{account?.ifrs_subsection}</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-1">
                            {account?.is_control_account && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Control
                              </span>
                            )}
                            {account?.is_bank_account && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Bank
                              </span>
                            )}
                            {account?.is_cash_account && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                Cash
                              </span>
                            )}
                            {!account?.allows_posting && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                No Posting
                              </span>
                            )}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              account?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                            }`}>
                              {account?.status || 'Active'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="font-medium text-foreground">
                              {formatCurrency(account?.current_balance)}
                            </p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {account?.account_subtype?.replace(/_/g, ' ')}
                            </p>
                          </div>

                          <div className="flex items-center space-x-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditAccount(account)}
                            >
                              <Icon name="Edit2" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeactivateAccount(account?.id)}
                              disabled={account?.is_system_account}
                            >
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Template Selection Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-card rounded-lg border border-border shadow-xl max-w-md w-full mx-4">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Apply IFRS Template</h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowTemplateModal(false)}>
                    <Icon name="X" size={16} />
                  </Button>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-muted-foreground mb-4">
                  Choose an IFRS-compliant template to create your complete chart of accounts structure.
                </p>
                
                {/* Show loading state while templates are being loaded */}
                {loading && templates?.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Icon name="Loader2" className="w-5 h-5 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">Loading templates...</span>
                  </div>
                ) : templates?.length > 0 ? (
                  <>
                    <Select
                      label="Select Template"
                      options={templates?.map(t => ({
                        value: t?.id,
                        label: t?.template_name,
                        description: t?.template_description
                      }))}
                      value={selectedTemplate}
                      onChange={setSelectedTemplate}
                      placeholder="Choose an IFRS template..."
                    />

                    {selectedTemplate && (
                      <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">
                          {templates?.find(t => t?.id === selectedTemplate)?.template_description}
                        </p>
                        <div className="mt-2 text-xs text-primary">
                          ✅ IFRS Compliant Template
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="AlertTriangle" className="w-12 h-12 mx-auto text-orange-500 mb-3" />
                    <h4 className="font-medium text-foreground mb-2">No Templates Available</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      IFRS templates are not currently available in your system.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <Icon name="Info" className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-700">
                          <strong>What you can do:</strong><br/>
                          • Contact your administrator to set up IFRS templates<br/>
                          • Create accounts manually using the "Add Account" button<br/>
                          • Check back later for template availability
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-border">
                <div className="flex space-x-3">
                  <Button variant="ghost" className="flex-1" onClick={() => setShowTemplateModal(false)}>
                    Cancel
                  </Button>
                  {templates?.length > 0 && (
                    <Button
                      variant="default"
                      className="flex-1"
                      onClick={handleApplyTemplate}
                      disabled={!selectedTemplate || applyingTemplate}
                    >
                      {applyingTemplate ? (
                        <>
                          <Icon name="Loader2" className="w-4 h-4 animate-spin mr-2" />
                          Applying...
                        </>
                      ) : (
                        'Apply Template'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trial Balance Modal */}
        {showTrialBalance && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-card rounded-lg border border-border shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Trial Balance</h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowTrialBalance(false)}>
                    <Icon name="X" size={16} />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-96">
                {trialBalance?.length > 0 ? (
                  <div className="space-y-4">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-2">Account Code</th>
                          <th className="text-left p-2">Account Name</th>
                          <th className="text-right p-2">Debit</th>
                          <th className="text-right p-2">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trialBalance?.map((account, index) => (
                          <tr key={index} className="border-b border-border/50">
                            <td className="p-2 font-mono text-sm">{account?.account_code}</td>
                            <td className="p-2">{account?.account_name}</td>
                            <td className="p-2 text-right">
                              {account?.debit_balance ? formatCurrency(account?.debit_balance) : '-'}
                            </td>
                            <td className="p-2 text-right">
                              {account?.credit_balance ? formatCurrency(account?.credit_balance) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground">No trial balance data available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartOfAccounts;